import { useEffect, useMemo, useState } from 'react';
import { DirectionsRenderer, GoogleMap, Marker } from '@react-google-maps/api';
import { ITransportGroupStop } from 'core/api/transport-groups/transport-groups-api-interface';

/**
 * A run, drawn in the order it will be driven.
 *
 * Separate from the deliveries `RouteMap` rather than shared with it: that one
 * is keyed on delivery status and starts from the pharmacy's fixed coordinates,
 * where a run starts from whichever site the office configured and its stops
 * are collections and deliveries rather than drops. The library, the shape and
 * the approach are the same; the data has nothing in common.
 *
 * The driving directions are Google's and the sequence is GraphHopper's. They
 * can disagree on the road taken between two stops — the order is what matters
 * here, and drawing it is what makes an unreasonable one obvious.
 */

interface IProps {
  stops: ITransportGroupStop[];
  /** Where the run sets off from, which is not a stop. */
  start?: { latitude: number; longitude: number };
}

const CONTAINER = { width: '100%', height: '320px' };

const TransportRunMap = ({ stops, start }: IProps) => {
  const [directions, setDirections] = useState<google.maps.DirectionsResult>();

  const points = useMemo(
    () =>
      stops
        .filter((stop) => stop.address?.latitude && stop.address?.longitude)
        .map((stop) => ({ lat: stop.address!.latitude, lng: stop.address!.longitude, stop })),
    [stops],
  );

  useEffect(() => {
    // One route, in sequence. Nothing to draw a line between with fewer than
    // two points, and the markers alone still say where the work is.
    if (!points.length) return;

    const origin = start ? { lat: start.latitude, lng: start.longitude } : points[0];
    const waypoints = (start ? points : points.slice(1)).slice(0, -1).map((point) => ({
      location: { lat: point.lat, lng: point.lng },
    }));
    const destination = points[points.length - 1];

    let cancelled = false;
    new google.maps.DirectionsService()
      .route({
        origin: { lat: origin.lat, lng: origin.lng },
        destination: { lat: destination.lat, lng: destination.lng },
        waypoints,
        travelMode: google.maps.TravelMode.DRIVING,
      })
      // A route Google cannot draw is not a reason to show nothing: the stops
      // and their order are the point, and the line is the illustration.
      .then((result) => !cancelled && setDirections(result))
      .catch(() => undefined);

    return () => {
      cancelled = true;
    };
  }, [points, start]);

  const centre = points[0] ?? (start ? { lat: start.latitude, lng: start.longitude } : { lat: 52.9112, lng: -1.5145 });

  return (
    <GoogleMap mapContainerStyle={CONTAINER} zoom={10} center={{ lat: centre.lat, lng: centre.lng }}>
      {directions && <DirectionsRenderer directions={directions} options={{ suppressMarkers: true }} />}

      {start && (
        <Marker
          position={{ lat: start.latitude, lng: start.longitude }}
          label={{ text: 'S', color: 'white', fontWeight: 'bold' }}
        />
      )}

      {points.map((point) => (
        <Marker
          key={`${point.stop.sequence}-${point.stop.site}-${point.stop.point}`}
          position={{ lat: point.lat, lng: point.lng }}
          // Numbered to match the list underneath, because the question being
          // asked of the map is "why is it going there next".
          label={{ text: String(point.stop.sequence), color: 'white', fontWeight: 'bold' }}
        />
      ))}
    </GoogleMap>
  );
};

export default TransportRunMap;
